import { useState, useMemo, ReactNode } from 'react';
import { Box, Table, TableBody, TableHead, TableRow, Chip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { abyssColors } from '@/theme/colors';
import { HistoryEntry, SectionChange, FieldChange } from './historyTypes';
import {
  TimelineRow,
  TimelineTrack,
  TimelineDot,
  TimelineLine,
  TimelineContent,
  EntryCard,
  EntryTimestamp,
  EntryDescription,
  HoursAccordion,
  HoursAccordionSummary,
  HoursAccordionDetails,
  SectionHeaderRow,
  SectionTitle,
  SectionValueStrikethrough,
  SectionValueUpdated,
  SectionTableContainer,
  SectionHeaderCell,
  SectionBodyCell,
  ChangeValue,
  SectionLabel,
  shiftChipStyles,
} from './styles';
import { ArrowForward } from '@mui/icons-material';
import { titleCase } from '@/utils';

type Props = {
  entry: HistoryEntry;
  isLast: boolean;
};

const formatTimestamp = (
  timestamp: string,
  userTimezone?: string
): { date: string; time: string } => {
  const numericTimestamp = Number(timestamp);
  const date = Number.isNaN(numericTimestamp) ? new Date(timestamp) : new Date(numericTimestamp);

  if (Number.isNaN(date.getTime())) {
    return { date: 'Invalid Date', time: '' };
  }

  const timeZone = userTimezone || 'UTC';

  // Use Intl.DateTimeFormat to get parts in the specified timezone
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  const dateParts = dateFormatter.formatToParts(date);
  const timeParts = timeFormatter.formatToParts(date);

  const day = dateParts.find((part) => part.type === 'day')?.value || '';
  const month = dateParts.find((part) => part.type === 'month')?.value || '';
  const year = dateParts.find((part) => part.type === 'year')?.value || '';
  const hour = timeParts.find((part) => part.type === 'hour')?.value || '';
  const minute = timeParts.find((part) => part.type === 'minute')?.value || '';
  const dayPeriod = timeParts.find((part) => part.type === 'dayPeriod')?.value || '';

  return {
    date: `${month}/${day}/${year}`,
    time: `${hour}:${minute} ${dayPeriod}`,
  };
};

const getChangeDirection = (change: number): 'positive' | 'negative' | 'neutral' => {
  if (change > 0) return 'positive';
  if (change < 0) return 'negative';
  return 'neutral';
};

const formatNumeric = (value: number): number => {
  return parseFloat(value.toFixed(3));
};

const formatChange = (change: number) => {
  const formatted = formatNumeric(change);
  if (formatted > 0) return `+${formatted}`;
  return formatted.toString();
};

const formatHistoryUnit = (unit: string): string => {
  switch (unit) {
    case 'h':
      return 'hr';
    case '€':
      return '$';
    default:
      return unit;
  }
};

const isCostUnit = (unit: string): boolean => unit === '$' || unit === '€';

const formatValueWithUnit = (value: number, unit: string): string => {
  const formattedUnit = formatHistoryUnit(unit);
  const formattedValue = String(formatNumeric(value));
  return isCostUnit(unit)
    ? `${formattedUnit} ${formattedValue}`
    : `${formattedValue} ${formattedUnit}`;
};

const getUnitLabel = (unit: string): string => {
  switch (unit) {
    case 'h':
      return 'hours';
    case 'm²':
    case 'ft²':
      return `${unit} area`;
    case '$':
      return 'cost';
    case '€':
      return 'cost';
    default:
      return unit;
  }
};

const getUpdateDescription = (
  sections?: SectionChange[],
  standaloneFields?: FieldChange[]
): string => {
  const labels: string[] = [];
  if (sections) {
    labels.push(...sections.map((s) => getUnitLabel(s.unit)).filter(Boolean));
  }
  if (standaloneFields && standaloneFields.length > 0) {
    labels.push(...standaloneFields.map((f) => f.category));
  }
  const unique = [...new Set(labels)];
  if (unique.length === 0) return 'updated values';
  if (unique.length === 1) return `updated ${unique[0]}`;
  return `updated ${unique.slice(0, -1).join(', ')} and ${unique[unique.length - 1]}`;
};

const renderNumericFieldUpdatedCell = (field: FieldChange) => {
  if (field.deleted) {
    return (
      <Box component="span" sx={{ color: 'text.disabled', fontSize: 'inherit' }}>
        Deleted
      </Box>
    );
  }
  return formatNumeric(field.updated);
};

const SectionTable = ({ section }: { section: SectionChange }) => (
  <Box sx={{ mb: 2 }}>
    <SectionHeaderRow>
      <SectionLabel sx={{ minWidth: 150, flexShrink: 0 }}>{section.sectionTitle}</SectionLabel>
      <SectionValueStrikethrough>
        {formatValueWithUnit(section.totalBefore, section.unit)}
      </SectionValueStrikethrough>
      <SectionTitle>
        <ArrowForward sx={{ fontSize: '12px' }} />
      </SectionTitle>
      {section.deleted ? (
        <Box component="span" sx={{ color: 'text.disabled', fontSize: 'inherit' }}>
          Deleted
        </Box>
      ) : (
        <SectionValueUpdated>
          {formatValueWithUnit(section.totalUpdated, section.unit)}
        </SectionValueUpdated>
      )}
    </SectionHeaderRow>

    <SectionTableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <SectionHeaderCell sx={{ width: '40%' }}>Category</SectionHeaderCell>
            <SectionHeaderCell sx={{ width: '30%' }}>Before</SectionHeaderCell>
            <SectionHeaderCell sx={{ width: '30%' }}>Updated</SectionHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {section.fields.map((field, index) => (
            <TableRow key={index}>
              <SectionBodyCell sx={{ pl: 2 }}>{field.category}</SectionBodyCell>
              <SectionBodyCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SectionValueStrikethrough>
                    {formatNumeric(field.before)}
                  </SectionValueStrikethrough>
                  {field.change !== undefined && (
                    <ChangeValue changeDirection={getChangeDirection(field.change)}>
                      {formatChange(field.change)}
                    </ChangeValue>
                  )}
                </Box>
              </SectionBodyCell>
              <SectionBodyCell sx={{ fontWeight: 600 }}>
                {field.deleted ? (
                  <Box component="span" sx={{ color: 'text.disabled', fontSize: 'inherit' }}>
                    Deleted
                  </Box>
                ) : (
                  formatNumeric(field.updated)
                )}
              </SectionBodyCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </SectionTableContainer>
  </Box>
);

const HoursUpdateEntry = ({
  entry,
  userTimezone,
}: {
  entry: HistoryEntry;
  userTimezone?: string;
}) => {
  const [expanded, setExpanded] = useState(false);
  const numericFields = entry.standaloneFields?.filter((f) => !f.isText) ?? [];
  const textFields = entry.standaloneFields?.filter((f) => f.isText) ?? [];

  return (
    <HoursAccordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
      <HoursAccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: abyssColors.primary[400] }} />}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <EntryTimestamp>
            <span>{formatTimestamp(entry.timestamp, userTimezone).date}</span>
            <span>{formatTimestamp(entry.timestamp, userTimezone).time}</span>
            {entry.shift && (
              <Chip label={titleCase(entry.shift)} size="small" sx={shiftChipStyles} />
            )}
          </EntryTimestamp>
          <SectionTitle>
            {entry.userName} {getUpdateDescription(entry.sections, entry.standaloneFields)}
          </SectionTitle>
          {!expanded && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
              {entry.sections?.map((section, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EntryDescription sx={{ minWidth: 100, flexShrink: 0 }}>
                    {section.sectionTitle}
                  </EntryDescription>
                  <SectionValueStrikethrough>
                    {formatValueWithUnit(section.totalBefore, section.unit)}
                  </SectionValueStrikethrough>
                  <SectionTitle>
                    <ArrowForward sx={{ fontSize: '12px' }} />
                  </SectionTitle>
                  {section.deleted ? (
                    <Box component="span" sx={{ color: 'text.disabled', fontSize: 'inherit' }}>
                      Deleted
                    </Box>
                  ) : (
                    <SectionValueUpdated>
                      {formatValueWithUnit(section.totalUpdated, section.unit)}
                    </SectionValueUpdated>
                  )}
                </Box>
              ))}
              {entry.standaloneFields?.map((field, index) => {
                let textDisplayValue: string;
                if (field.textUpdated) {
                  textDisplayValue =
                    field.textUpdated.length > 60
                      ? `${field.textUpdated.slice(0, 60)}…`
                      : field.textUpdated;
                } else {
                  textDisplayValue = field.textAction === 'added' ? 'Added' : 'Updated';
                }

                let textContent: ReactNode;
                if (field.deleted) {
                  textContent = (
                    <Box component="span" sx={{ color: 'text.disabled', fontSize: 'inherit' }}>
                      Deleted
                    </Box>
                  );
                } else {
                  textContent = (
                    <SectionValueUpdated
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '20rem',
                        display: 'inline-block',
                      }}
                    >
                      {textDisplayValue}
                    </SectionValueUpdated>
                  );
                }

                return (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <EntryDescription sx={{ minWidth: 100, flexShrink: 0 }}>
                      {field.category}
                    </EntryDescription>
                    {field.isText ? (
                      textContent
                    ) : (
                      <>
                        <SectionValueStrikethrough>
                          {formatNumeric(field.before)}
                        </SectionValueStrikethrough>
                        <SectionTitle>
                          <ArrowForward sx={{ fontSize: '12px' }} />
                        </SectionTitle>
                        {field.deleted ? (
                          <Box
                            component="span"
                            sx={{ color: 'text.disabled', fontSize: 'inherit' }}
                          >
                            Deleted
                          </Box>
                        ) : (
                          <SectionValueUpdated>{formatNumeric(field.updated)}</SectionValueUpdated>
                        )}
                      </>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </HoursAccordionSummary>
      <HoursAccordionDetails>
        {entry.sections?.map((section, index) => (
          <SectionTable key={index} section={section} />
        ))}
        {numericFields.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <SectionTableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SectionHeaderCell sx={{ width: '40%' }}>Field</SectionHeaderCell>
                    <SectionHeaderCell sx={{ width: '30%' }}>Before</SectionHeaderCell>
                    <SectionHeaderCell sx={{ width: '30%' }}>Updated</SectionHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {numericFields.map((field, index) => (
                    <TableRow key={index}>
                      <SectionBodyCell sx={{ pl: 2 }}>{field.category}</SectionBodyCell>
                      <SectionBodyCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SectionValueStrikethrough>
                            {formatNumeric(field.before)}
                          </SectionValueStrikethrough>
                          {field.change !== undefined && (
                            <ChangeValue changeDirection={getChangeDirection(field.change)}>
                              {formatChange(field.change)}
                            </ChangeValue>
                          )}
                        </Box>
                      </SectionBodyCell>
                      <SectionBodyCell sx={{ fontWeight: 600 }}>
                        {renderNumericFieldUpdatedCell(field)}
                      </SectionBodyCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </SectionTableContainer>
          </Box>
        )}
        {textFields.map((field, index) => (
          <Box key={`${field.category}-${index}`} sx={{ mb: 2 }}>
            <Typography
              sx={{ fontWeight: 600, fontSize: '12px', color: abyssColors.primary[400], mb: 0.75 }}
            >
              {field.category}
            </Typography>
            {field.deleted ? (
              <Box component="span" sx={{ color: 'text.disabled', fontSize: '12px' }}>
                Deleted
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {field.textBefore && (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '1.5rem',
                        mb: 0.25,
                        color: abyssColors.primary[400],
                        fontWeight: 500,
                      }}
                    >
                      Before
                    </Typography>
                    <EntryDescription sx={{ color: 'text.secondary', opacity: 0.7 }}>
                      {field.textBefore}
                    </EntryDescription>
                  </Box>
                )}
                <Box>
                  {field.textBefore && (
                    <Typography
                      sx={{
                        fontSize: '1.5rem',
                        mb: 0.25,
                        color: abyssColors.primary[400],
                        fontWeight: 500,
                      }}
                    >
                      Updated
                    </Typography>
                  )}
                  <EntryDescription>{field.textUpdated}</EntryDescription>
                </Box>
              </Box>
            )}
          </Box>
        ))}
      </HoursAccordionDetails>
    </HoursAccordion>
  );
};

const formatKeyLabel = (key?: string): string => {
  if (!key) return 'note';
  const parts = key.split('.');
  const trimmed = parts.length > 2 ? parts.slice(2).join('.') : parts.slice(-1).join('.');
  return trimmed.replace(/[._]/g, ' ');
};

const getCommentAction = (text?: string): 'added' | 'updated' => {
  return text?.trim() ? 'added' : 'updated';
};

const getCommentLabel = (key?: string): string => {
  if (key?.includes('discrepancy_reason')) {
    return 'discrepancy';
  }
  return formatKeyLabel(key);
};

const OperationalDelayEntry = ({
  entry,
  userTimezone,
}: {
  entry: HistoryEntry;
  userTimezone?: string;
}) => (
  <EntryCard>
    <EntryTimestamp>
      <span>{formatTimestamp(entry.timestamp, userTimezone).date}</span>
      <span>{formatTimestamp(entry.timestamp, userTimezone).time}</span>
    </EntryTimestamp>
    <SectionTitle sx={{ mb: 1 }}>
      {entry.userName} added {formatKeyLabel(entry.commentKey)} note
    </SectionTitle>
    <EntryDescription>{entry.description}</EntryDescription>
  </EntryCard>
);

const documentAttachmentTargetLabel = (
  target: HistoryEntry['documentAttachmentTarget']
): string => {
  if (target === 'job_phase_form') {
    return 'the job phase form';
  }
  return 'the work log';
};

const DocumentAttachedEntry = ({
  entry,
  userTimezone,
}: {
  entry: HistoryEntry;
  userTimezone?: string;
}) => {
  const count = entry.documentCount ?? 1;
  const docText = count === 1 ? 'a document' : `${count} documents`;
  const action = entry.documentAction === 'removed' ? 'removed' : 'attached';
  const preposition = entry.documentAction === 'removed' ? 'from' : 'to';
  const logTargetLabel = documentAttachmentTargetLabel(entry.documentAttachmentTarget);

  return (
    <EntryCard>
      <EntryTimestamp>
        <span>{formatTimestamp(entry.timestamp, userTimezone).date}</span>
        <span>{formatTimestamp(entry.timestamp, userTimezone).time}</span>
        {entry.shift && <Chip label={titleCase(entry.shift)} size="small" sx={shiftChipStyles} />}
      </EntryTimestamp>
      <SectionTitle>
        {entry.userName} {action} {docText} {preposition} {logTargetLabel}
        {entry.documentDate ? ` for ${entry.documentDate}` : ''}.
      </SectionTitle>
    </EntryCard>
  );
};

const CommentEntry = ({ entry, userTimezone }: { entry: HistoryEntry; userTimezone?: string }) => (
  <EntryCard>
    <EntryTimestamp>
      <span>{formatTimestamp(entry.timestamp, userTimezone).date}</span>
      <span>{formatTimestamp(entry.timestamp, userTimezone).time}</span>
      {entry.shift && <Chip label={titleCase(entry.shift)} size="small" sx={shiftChipStyles} />}
    </EntryTimestamp>
    <SectionTitle sx={{ mb: 1 }}>
      {entry.userName} {getCommentAction(entry.commentText)} {getCommentLabel(entry.commentKey)}{' '}
      note
    </SectionTitle>
    {entry.commentText?.trim() ? <EntryDescription>{entry.commentText}</EntryDescription> : null}
  </EntryCard>
);

const JobPhaseFormEntry = ({
  entry,
  userTimezone,
}: {
  entry: HistoryEntry;
  userTimezone?: string;
}) => (
  <EntryCard>
    <EntryTimestamp>
      <span>{formatTimestamp(entry.timestamp, userTimezone).date}</span>
      <span>{formatTimestamp(entry.timestamp, userTimezone).time}</span>
      {entry.shift ? (
        <Chip label={titleCase(entry.shift)} size="small" sx={shiftChipStyles} />
      ) : null}
    </EntryTimestamp>
    <SectionTitle>
      {entry.userName} {entry.description ?? 'updated the job phase form'}.
    </SectionTitle>
  </EntryCard>
);

export const KpiPanelHistoryEntry = ({ entry, isLast }: Props) => {
  const userTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  return (
    <TimelineRow isLast={isLast}>
      <TimelineTrack>
        <TimelineDot />
        <TimelineLine />
      </TimelineTrack>

      <TimelineContent isLast={isLast}>
        {entry.type === 'hours_update' && (
          <HoursUpdateEntry entry={entry} userTimezone={userTimezone} />
        )}
        {entry.type === 'operational_delay' && (
          <OperationalDelayEntry entry={entry} userTimezone={userTimezone} />
        )}
        {entry.type === 'document_attached' && (
          <DocumentAttachedEntry entry={entry} userTimezone={userTimezone} />
        )}
        {entry.type === 'comment' && <CommentEntry entry={entry} userTimezone={userTimezone} />}
        {entry.type === 'qaqc_form' && (
          <JobPhaseFormEntry entry={entry} userTimezone={userTimezone} />
        )}
      </TimelineContent>
    </TimelineRow>
  );
};
